import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import type { FosterCarerRegister, FosterCarerRegisterDetail, VettingSubjectRole } from '../../../lib/types';
import { useApiQuery } from '../../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../../ui/components';

const STAFF_ROLES = ['SOCIAL_WORKER', 'SENIOR_SW'];
const SENIOR_ROLES = ['SENIOR_SW'];

/**
 * Foster carer register — GET/POST /api/v1/m27/foster-carers/*. The
 * application itself (POST /foster-carers) is a citizen self-service
 * endpoint gated on the applicant's own verified NIN (ctx.nin must equal
 * applicantNin) — there is no staff-side "lodge an application on a
 * citizen's behalf" endpoint, so this screen (correctly) has no "+ New
 * application" action; see this app's build report for the gap. What staff
 * actually own end to end is exactly what this screen covers: vetting,
 * training, the approve/reject/suspend/reinstate decision, and placements.
 */
export function FosterCare() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isStaff = roles.some((r) => STAFF_ROLES.includes(r));
  const isSenior = roles.some((r) => SENIOR_ROLES.includes(r));

  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '50' });
    if (statusFilter) p.set('status', statusFilter);
    if (q) p.set('q', q);
    return `/api/v1/m27/foster-carers?${p}`;
  }, [statusFilter, q]);
  const listQ = useApiQuery<{ items: FosterCarerRegister[]; nextCursor: string | null }>(isStaff ? query : null);

  if (!isStaff) {
    return (
      <div>
        <PageHeader title="Foster Care" />
        <ErrorNote message="This view requires the Social Worker or Senior Social Worker role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Foster Carer Register"
        subtitle="Vetting, training, approval and placements — applications are lodged by the citizen themselves via One Citizen self-service"
      />
      <div className="mb-4 flex flex-wrap gap-3">
        <input className="field-input max-w-xs" placeholder="Search applicant or ref…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input max-w-[180px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="APPLIED">Applied</option>
          <option value="VETTING">Vetting</option>
          <option value="TRAINING">Training</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="WITHDRAWN">Withdrawn</option>
        </select>
      </div>

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.error ? (
          <ErrorNote message={listQ.error} />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No foster carer applications found" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Applicant</th>
                <th className="table-head">District</th>
                <th className="table-head">Status</th>
                <th className="table-head">Capacity</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((f) => (
                <tr key={f.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{f.refNo}</td>
                  <td className="table-cell text-xs">{f.applicantName}</td>
                  <td className="table-cell text-xs">{f.district.replaceAll('-', ' ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={f.status} />
                  </td>
                  <td className="table-cell text-xs">{f.approvedCapacity ?? '—'}</td>
                  <td className="table-cell text-right">
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setOpenId(f.id)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId ? (
        <FosterDetailModal id={openId} isSenior={isSenior} onClose={() => setOpenId(null)} onChanged={() => listQ.reload()} />
      ) : null}
    </div>
  );
}

function FosterDetailModal({
  id,
  isSenior,
  onClose,
  onChanged,
}: {
  id: string;
  isSenior: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<FosterCarerRegisterDetail>(`/api/v1/m27/foster-carers/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    detailQ.reload();
    onChanged();
  };

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  // vetting
  const [showAddVetting, setShowAddVetting] = useState(false);
  const [vSubjectNin, setVSubjectNin] = useState('');
  const [vSubjectName, setVSubjectName] = useState('');
  const [vRole, setVRole] = useState<VettingSubjectRole>('HOUSEHOLD_MEMBER');
  const [vSerial, setVSerial] = useState('');
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [verifyHint, setVerifyHint] = useState<'CLEAN' | 'RECORD_DISCLOSED'>('CLEAN');
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decideChoice, setDecideChoice] = useState<'ACCEPT' | 'UNSUITABLE'>('ACCEPT');
  const [decideReason, setDecideReason] = useState('');

  // training
  const [showAddTraining, setShowAddTraining] = useState(false);
  const [courseName, setCourseName] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');

  // decision
  const [approvedCapacity, setApprovedCapacity] = useState('1');
  const [decisionReason, setDecisionReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [showSuspend, setShowSuspend] = useState(false);

  // placements
  const [showAddPlacement, setShowAddPlacement] = useState(false);
  const [childNin, setChildNin] = useState('');
  const [childName, setChildName] = useState('');
  const [placementCaseId, setPlacementCaseId] = useState('');
  const [placementStart, setPlacementStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [endingId, setEndingId] = useState<string | null>(null);
  const [endReason, setEndReason] = useState('');

  const data = detailQ.data;
  const canApprove = data && (['APPLIED', 'VETTING', 'TRAINING'] as string[]).includes(data.status);

  return (
    <Modal title={data ? `Foster carer — ${data.refNo}` : 'Foster carer application'} onClose={onClose} wide>
      {detailQ.loading ? (
        <Spinner />
      ) : detailQ.error ? (
        <ErrorNote message={detailQ.error} />
      ) : data ? (
        <div className="space-y-5">
          {error ? <ErrorNote message={error} /> : null}

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={data.status} />
            </div>
            <div className="mt-1 text-lg font-bold text-slate-900">{data.applicantName}</div>
            <div className="text-xs text-slate-500">
              NIN {data.applicantNin} · District {data.district.replaceAll('-', ' ')}
              {data.approvedCapacity != null ? ` · Approved capacity ${data.approvedCapacity}` : ''}
            </div>
            {data.rejectReason ? <p className="mt-1 text-xs text-rose-600">Rejected: {data.rejectReason}</p> : null}
            {data.suspendReason ? <p className="mt-1 text-xs text-amber-600">Suspended: {data.suspendReason}</p> : null}
            {data.householdMembers.length > 0 ? (
              <ul className="mt-2 text-xs text-slate-600">
                {data.householdMembers.map((m, i) => (
                  <li key={i}>
                    {m.relation}: {m.name}
                    {m.dob ? ` (DOB ${formatDate(m.dob)})` : ''}
                    {m.nin ? ` — ${m.nin}` : ''}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">Vetting checks</h3>
              <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setShowAddVetting((v) => !v)}>
                + Add check
              </button>
            </div>
            {data.vettingChecks.length === 0 ? (
              <EmptyState title="No vetting checks recorded" />
            ) : (
              <ul className="space-y-1.5">
                {data.vettingChecks.map((v) => (
                  <li key={v.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {v.subjectName} ({v.role.replaceAll('_', ' ')})
                      </span>
                      <StatusBadge status={v.result} />
                    </div>
                    {v.notes ? <p className="whitespace-pre-wrap text-xs text-slate-500">{v.notes}</p> : null}
                    {v.result === 'PENDING' && v.clearanceCertSerial ? (
                      verifyingId === v.id ? (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <select className="field-input !py-1 text-xs" value={verifyHint} onChange={(e) => setVerifyHint(e.target.value as typeof verifyHint)}>
                            <option value="CLEAN">Certificate states CLEAN</option>
                            <option value="RECORD_DISCLOSED">Certificate states RECORD_DISCLOSED</option>
                          </select>
                          <button
                            className="btn-primary !px-2 !py-0.5 text-xs"
                            disabled={busy}
                            onClick={() => run(async () => {
                              await api.post(`/api/v1/m27/foster-carers/${id}/vetting-checks/${v.id}/verify`, { resultHint: verifyHint });
                              setVerifyingId(null);
                            })}
                          >
                            Verify
                          </button>
                        </div>
                      ) : (
                        <button className="btn-secondary mt-1.5 !px-2 !py-0.5 text-xs" onClick={() => setVerifyingId(v.id)}>
                          Verify against DocVault
                        </button>
                      )
                    ) : v.result === 'PENDING' ? (
                      <p className="mt-1 text-xs text-slate-400">No clearance certificate serial on file yet.</p>
                    ) : null}
                    {v.result === 'DISCLOSED' && isSenior ? (
                      decidingId === v.id ? (
                        <div className="mt-1.5 space-y-1.5">
                          <select className="field-input !py-1 text-xs" value={decideChoice} onChange={(e) => setDecideChoice(e.target.value as typeof decideChoice)}>
                            <option value="ACCEPT">Accept — not disqualifying</option>
                            <option value="UNSUITABLE">Unsuitable — disqualifying</option>
                          </select>
                          <input className="field-input !py-1 text-xs" placeholder="Reason" value={decideReason} onChange={(e) => setDecideReason(e.target.value)} />
                          <button
                            className="btn-primary !px-2 !py-0.5 text-xs"
                            disabled={busy || decideReason.length < 5}
                            onClick={() => run(async () => {
                              await api.post(`/api/v1/m27/foster-carers/${id}/vetting-checks/${v.id}/decide`, { decision: decideChoice, reason: decideReason });
                              setDecidingId(null); setDecideReason('');
                            })}
                          >
                            Record suitability decision
                          </button>
                        </div>
                      ) : (
                        <button className="btn-secondary mt-1.5 !px-2 !py-0.5 text-xs" onClick={() => setDecidingId(v.id)}>
                          Record suitability decision
                        </button>
                      )
                    ) : v.result === 'DISCLOSED' ? (
                      <p className="mt-1 text-xs text-slate-400">Needs a Senior Social Worker's suitability decision.</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {showAddVetting ? (
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Subject NIN">
                    <input className="field-input" value={vSubjectNin} onChange={(e) => setVSubjectNin(e.target.value)} />
                  </Field>
                  <Field label="Subject name">
                    <input className="field-input" value={vSubjectName} onChange={(e) => setVSubjectName(e.target.value)} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Role">
                    <select className="field-input" value={vRole} onChange={(e) => setVRole(e.target.value as VettingSubjectRole)}>
                      <option value="APPLICANT">Applicant</option>
                      <option value="HOUSEHOLD_MEMBER">Household member</option>
                    </select>
                  </Field>
                  <Field label="Clearance cert serial (if presented)">
                    <input className="field-input" value={vSerial} onChange={(e) => setVSerial(e.target.value)} />
                  </Field>
                </div>
                <div className="flex justify-end">
                  <button
                    className="btn-primary !py-1 text-xs"
                    disabled={busy || !vSubjectNin || !vSubjectName}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/foster-carers/${id}/vetting-checks`, { subjectNin: vSubjectNin, subjectName: vSubjectName, role: vRole, clearanceCertSerial: vSerial || undefined });
                      setVSubjectNin(''); setVSubjectName(''); setVSerial(''); setShowAddVetting(false);
                    })}
                  >
                    Add vetting check
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">Training</h3>
              <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setShowAddTraining((v) => !v)}>
                + Schedule
              </button>
            </div>
            {data.trainings.length === 0 ? (
              <EmptyState title="No training scheduled" />
            ) : (
              <ul className="space-y-1.5">
                {data.trainings.map((t) => (
                  <li key={t.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div>
                      <span className="font-medium">{t.courseName}</span>
                      {t.scheduledFor ? <span className="ml-2 text-xs text-slate-400">{formatDate(t.scheduledFor)}</span> : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={t.status} />
                      {t.status === 'SCHEDULED' ? (
                        <button
                          className="btn-secondary !px-2 !py-0.5 text-xs"
                          disabled={busy}
                          onClick={() => run(async () => { await api.post(`/api/v1/m27/foster-carers/${id}/training/${t.id}/complete`, {}); })}
                        >
                          Mark complete
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {showAddTraining ? (
              <div className="mt-2 flex items-end gap-2 rounded-lg border border-slate-200 p-3">
                <Field label="Course name" className="flex-1">
                  <input className="field-input" value={courseName} onChange={(e) => setCourseName(e.target.value)} />
                </Field>
                <Field label="Scheduled for">
                  <input className="field-input" type="date" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} />
                </Field>
                <button
                  className="btn-primary !py-2"
                  disabled={busy || !courseName}
                  onClick={() => run(async () => {
                    await api.post(`/api/v1/m27/foster-carers/${id}/training`, { courseName, scheduledFor: scheduledFor || undefined });
                    setCourseName(''); setScheduledFor(''); setShowAddTraining(false);
                  })}
                >
                  Schedule
                </button>
              </div>
            ) : null}
          </div>

          {isSenior ? (
            <div>
              <h3 className="mb-1 text-sm font-bold text-slate-700">Decision</h3>
              <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 p-3">
                {canApprove ? (
                  <>
                    <Field label="Approved capacity">
                      <input className="field-input w-24" type="number" min={1} max={10} value={approvedCapacity} onChange={(e) => setApprovedCapacity(e.target.value)} />
                    </Field>
                    <button
                      className="btn-success !py-2"
                      disabled={busy}
                      onClick={() => run(async () => { await api.post(`/api/v1/m27/foster-carers/${id}/decision`, { decision: 'APPROVE', approvedCapacity: Number.parseInt(approvedCapacity, 10) }); })}
                    >
                      Approve
                    </button>
                    <button className="btn-danger !py-2" onClick={() => setShowReject((v) => !v)}>
                      Reject
                    </button>
                  </>
                ) : null}
                {data.status === 'APPROVED' ? (
                  <button className="btn-secondary !py-2" onClick={() => setShowSuspend((v) => !v)}>
                    Suspend
                  </button>
                ) : null}
                {data.status === 'SUSPENDED' ? (
                  <button
                    className="btn-secondary !py-2"
                    disabled={busy}
                    onClick={() => run(async () => { await api.post(`/api/v1/m27/foster-carers/${id}/decision`, { decision: 'REINSTATE' }); })}
                  >
                    Reinstate
                  </button>
                ) : null}
              </div>
              {showReject || showSuspend ? (
                <div className="mt-2 flex items-end gap-2 rounded-lg border border-slate-200 p-3">
                  <Field label="Reason" className="flex-1">
                    <input className="field-input" value={decisionReason} onChange={(e) => setDecisionReason(e.target.value)} />
                  </Field>
                  <button
                    className="btn-danger !py-2"
                    disabled={busy || decisionReason.length < 5}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/foster-carers/${id}/decision`, { decision: showReject ? 'REJECT' : 'SUSPEND', reason: decisionReason });
                      setDecisionReason(''); setShowReject(false); setShowSuspend(false);
                    })}
                  >
                    Confirm {showReject ? 'reject' : 'suspend'}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">Placements</h3>
              {data.status === 'APPROVED' ? (
                <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setShowAddPlacement((v) => !v)}>
                  + Place child
                </button>
              ) : null}
            </div>
            {data.placements.length === 0 ? (
              <EmptyState title="No placements" />
            ) : (
              <ul className="space-y-1.5">
                {data.placements.map((p) => (
                  <li key={p.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{p.childName}</span>
                      <StatusBadge status={p.status} />
                    </div>
                    <div className="text-xs text-slate-500">
                      {p.childNin} · from {formatDate(p.startDate)}
                      {p.endDate ? ` to ${formatDate(p.endDate)}` : ''}
                    </div>
                    {p.endReason ? <p className="text-xs text-slate-400">{p.endReason}</p> : null}
                    {p.status === 'ACTIVE' ? (
                      endingId === p.id ? (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <input className="field-input !py-1 text-xs" placeholder="End reason" value={endReason} onChange={(e) => setEndReason(e.target.value)} />
                          <button
                            className="btn-secondary !px-2 !py-0.5 text-xs"
                            disabled={busy || endReason.length < 3}
                            onClick={() => run(async () => {
                              await api.post(`/api/v1/m27/foster-carers/${id}/placements/${p.id}/end`, { endReason });
                              setEndingId(null); setEndReason('');
                            })}
                          >
                            Confirm end
                          </button>
                        </div>
                      ) : (
                        <button className="btn-secondary mt-1.5 !px-2 !py-0.5 text-xs" onClick={() => setEndingId(p.id)}>
                          End placement
                        </button>
                      )
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {showAddPlacement ? (
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Child NIN">
                    <input className="field-input" value={childNin} onChange={(e) => setChildNin(e.target.value)} />
                  </Field>
                  <Field label="Child name">
                    <input className="field-input" value={childName} onChange={(e) => setChildName(e.target.value)} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Related social case ID (optional)">
                    <input className="field-input" value={placementCaseId} onChange={(e) => setPlacementCaseId(e.target.value)} />
                  </Field>
                  <Field label="Start date">
                    <input className="field-input" type="date" value={placementStart} onChange={(e) => setPlacementStart(e.target.value)} />
                  </Field>
                </div>
                <div className="flex justify-end">
                  <button
                    className="btn-primary !py-1 text-xs"
                    disabled={busy || !childNin || !childName}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/foster-carers/${id}/placements`, { childNin, childName, caseId: placementCaseId || undefined, startDate: placementStart });
                      setChildNin(''); setChildName(''); setPlacementCaseId(''); setShowAddPlacement(false);
                    })}
                  >
                    Start placement
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
